import axios, {AxiosResponse} from 'axios';
import {CrewMember} from "./models/CrewMember";
import {Expense} from "./models/Expense";
import {Invoice} from "./models/Invoice";
import {IUser} from './models/IUser';
import {BaseBudget, Budget} from './models/Budget';
import {Layout} from "./models/Layout";
import {AvailabilityBoat, Boat, BoatFilters, Yacht} from "./models/Boat";
import {CalendarOffer} from "./models/SavedOffer";
import {MoneyIn} from "./models/MoneyIn";
import {Refund} from "./models/Refund";
import {Route} from "./models/Route";
import {Category} from "./models/Category";
import {IAffiliations} from "@/models/Affiliations";
import {ILocation} from "@/models/Location";
import {apiClient} from '@/services/client';

import { currentLocale } from "@/locales/localization";

const apiClient2 = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 500000,
});

const apiClient3 = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 500000,
});

apiClient3.interceptors.request.use(request => {
    return request
})

apiClient3.interceptors.response.use(response => {
    return response
})

apiClient2.interceptors.request.use((config) => {
    config.headers['ngrok-skip-browser-warning'] = 'true';
    return config;
}, (error) => {
    return Promise.reject(error);
});

const setAcceptLanguageInterceptor = (apiClientInstance) => {
    apiClientInstance.interceptors.request.use((config) => {
        const locale = currentLocale?.value || "en";
        config.headers["Accept-Language"] = locale;

        return config;
    }, (error) => {
        return Promise.reject(error);
    });
};


setAcceptLanguageInterceptor(apiClient);
setAcceptLanguageInterceptor(apiClient2);
setAcceptLanguageInterceptor(apiClient3);




export const getLocations = (regionIds?: number[], countryIds?: number[]): Promise<AxiosResponse<{
    _embedded: { locations: { id: number; name: string }[] }
}>> => {
    if (regionIds || countryIds) {
        const queryParams = new URLSearchParams();
        queryParams.set('page', '0');
        queryParams.set('size', '50');

        if (regionIds) {
            regionIds.forEach(id => {
                queryParams.append('regionIds', id.toString());
            });
        }

        if (countryIds) {
            countryIds.forEach(id => {
                queryParams.append('regionIds', id.toString());
            });
        }

        return apiClient2.get(`/location/search/findByGeolocation?${queryParams}`)
    }

    return apiClient2.get('/location?page=0&size=50');
};

export const getCategories = (): Promise<Category> => {

    return new Promise((resolve) => {
        apiClient.get('/yachtCategory')
            .then(response => {
                const order = [
                    41, // Sailing yacht
                    20, // Catamaran
                    45, // Motor yacht
                    21 // Gullet
                ];
                resolve(response.data._embedded.yachtCategories.sort((a, b) => {
                    return order.indexOf(a.id) - order.indexOf(b.id);
                }));
            })
            .catch((err) => {
                console.error('Error getting categories',err);
                return []
            });
    })
};

export const getCharterCompanies = (): Promise<AxiosResponse<{
    _embedded: { charterCompanies: { id: number; name: string }[] }
}>> => {
    return apiClient2.get('/charterCompany?page=0&size=50');
};

export const getEquipment = (): Promise<AxiosResponse<{
    _embedded: { equipments: { id: number; name: string }[] }
}>> => {
    return apiClient2.get('/equipment?page=0&size=50');
};

export const getRegions = (): Promise<AxiosResponse<{
    _embedded: { regions: { id: number; name: string }[] }
}>> => {
    return apiClient2.get('/region?page=0&size=50');
};

export const getSailTypes = (): Promise<AxiosResponse<{
    _embedded: { sailTypes: { id: number; name: string }[] }
}>> => {
    return apiClient2.get('/sailType');
};

export const getServices = (): Promise<AxiosResponse<{
    _embedded: { services: { id: number; name: string }[] }
}>> => {
    return apiClient2.get('/service?page=0&size=50');
};

export const getShipyards = (): Promise<AxiosResponse<{
    _embedded: { shipyards: { id: number; name: string }[] }
}>> => {
    return apiClient2.get('/shipyard');
};

export const getYachtModels = (): Promise<AxiosResponse<{
    _embedded: { yachtModels: { id: number; name: string }[] }
}>> => {
    return apiClient2.get('/yachtModel');
};

export const getCharterTypes = (): Promise<AxiosResponse<{
    _embedded: { charterTypes: { id: number; name: string } }
}>> => {
    return apiClient2.get('/charterType');
};

export const getCountries = (): Promise<AxiosResponse<{
    _embedded: { countries: { id: number; name: string }[] }
}>> => {
    return apiClient2.get('/country?size=260');
};

export const getUsers = (): Promise<AxiosResponse<IUser[]>> => {
    return apiClient.get('/api/v1/users');
};


export const getCrewList = (): Promise<AxiosResponse<CrewMember[]>> => {
    return apiClient.get('/api/v1/crew/list');
}

export const getCrewListCrew = (id: string): Promise<AxiosResponse<CrewMember[]>> => {
    return apiClient.get(`/api/v1/crew/list/${id}`);
}

export const addCrewMember = (uuid: string, crewData: Partial<CrewMember>): Promise<AxiosResponse<CrewMember>> => {
    return apiClient.post(`/api/v1/crew/list/${uuid}`, crewData);
};


export const getTripInvoices = (uuid: string): Promise<AxiosResponse<Invoice[]>> => {
    return apiClient.get(`/api/v1/trips/${uuid}/invoices`);
};

export const getBilling = (): Promise<AxiosResponse<Invoice[]>> => {
    return apiClient.get(`/api/v1/billing`);
};

export const getTripInvoice = (tripUuid: string, invoiceUuid: string): Promise<AxiosResponse<Invoice>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/invoices/${invoiceUuid}`);
};

export const createTripInvoice = (uuid: string, invoiceData: Partial<Invoice>): Promise<AxiosResponse<Invoice>> => {
    return apiClient.post(`/api/v1/trips/${uuid}/invoices`, invoiceData);
};

export const updateTripInvoice = (tripUuid: string, invoiceUuid: string, invoiceData: Partial<Invoice>): Promise<AxiosResponse<Invoice>> => {
    return apiClient.patch(`/api/v1/trips/${tripUuid}/invoices/${invoiceUuid}`, invoiceData);
};

export const deleteTripInvoice = (tripUuid: string, invoiceUuid: string): Promise<AxiosResponse<{
    message: string
}>> => {
    return apiClient.delete(`/api/v1/trips/${tripUuid}/invoices/${invoiceUuid}`);
};

export const getTripBudgetMoneyIn = (uuid: string): Promise<AxiosResponse<{ moneyIn: MoneyIn[] }>> => {
    return apiClient.get(`/api/v1/trips/${uuid}/budget/money-in`);
};

export const getTripBudgetMoneyInDetail = (tripUuid: string, budgetUuid: string): Promise<AxiosResponse<Budget>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/budget/money-in/${budgetUuid}`);
};

export const createTripBudgetMoneyIn = (uuid: string, budgetData: Partial<MoneyIn>): Promise<AxiosResponse<MoneyIn>> => {
    return apiClient.post(`/api/v1/trips/${uuid}/budget/money-in`, budgetData);
};

export const updateTripBudgetMoneyIn = (tripUuid: string, budgetUuid: string, budgetData: Partial<Budget>): Promise<AxiosResponse<Budget>> => {
    return apiClient.patch(`/api/v1/trips/${tripUuid}/budget/money-in/${budgetUuid}`, budgetData);
};

export const deleteTripBudgetMoneyIn = (tripUuid: string, budgetUuid: string): Promise<AxiosResponse<{
    message: string
}>> => {
    return apiClient.delete(`/api/v1/trips/${tripUuid}/budget/money-in/${budgetUuid}`);
};

export const getTripCurrentBalance = (uuid: string): Promise<AxiosResponse<BaseBudget>> => {
    return apiClient.get(`/api/v1/trips/${uuid}/budget/current-balance`);
};

export const getTripExpenses = (uuid: string): Promise<AxiosResponse<{ expenses: Expense[] }>> => {
    return apiClient.get(`/api/v1/trips/${uuid}/budget/expenses`);
};

export const getTripExpenseDetail = (tripUuid: string, expenseUuid: string): Promise<AxiosResponse<Expense>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/budget/expenses/${expenseUuid}`);
};

export const createTripExpense = (uuid: string, expenseData: Partial<Expense>): Promise<AxiosResponse<Expense>> => {
    return apiClient.post(`/api/v1/trips/${uuid}/budget/expenses`, expenseData);
};

export const updateTripExpense = (tripUuid: string, expenseUuid: string, expenseData: Partial<Expense>): Promise<AxiosResponse<Expense>> => {
    return apiClient.patch(`/api/v1/trips/${tripUuid}/budget/expenses/${expenseUuid}`, expenseData);
};

export const deleteTripExpense = (tripUuid: string, expenseUuid: string): Promise<AxiosResponse<{
    message: string
}>> => {
    return apiClient.delete(`/api/v1/trips/${tripUuid}/budget/expenses/${expenseUuid}`);
};

export const getTripRefunds = (tripUuid: string): Promise<AxiosResponse<{ refunds: Refund[] }>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/budget/refunds`);
};


export const voteSavedOffer = (tripId: string, offerId: string): Promise<AxiosResponse<any>> => {
    return apiClient.post(`/api/v1/trips/${tripId}/saved-offers/${offerId}/vote`);
};

export const getSavedOfferDetail = (tripUuid: string, offerUuid: string): Promise<AxiosResponse<any>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}`);
};

export const createSavedOffer = (uuid: string, offerData: Partial<any>): Promise<AxiosResponse<any>> => {
    return apiClient.post(`/api/v1/trips/${uuid}/saved-offers`, offerData);
};

export const updateSavedOffer = (tripUuid: string, offerUuid: string, offerData: Partial<any>): Promise<AxiosResponse<any>> => {
    return apiClient.patch(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}`, offerData);
};

export const deleteSavedOffer = (tripUuid: string, offerUuid: string): Promise<AxiosResponse<{ message: string }>> => {
    return apiClient.delete(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}`);
};

export const getSavedOfferPortDetail = (tripUuid: string, offerUuid: string, portUuid: string): Promise<AxiosResponse<any>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}`);
};

export const createSavedOfferPort = (tripUuid: string, offerUuid: string, portUuid: string, portData: Partial<any>): Promise<AxiosResponse<any>> => {
    return apiClient.post(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}`, portData);
};

export const updateSavedOfferPort = (tripUuid: string, offerUuid: string, portUuid: string, portData: Partial<any>): Promise<AxiosResponse<any>> => {
    return apiClient.patch(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}`, portData);
};

export const deleteSavedOfferPort = (tripUuid: string, offerUuid: string, portUuid: string): Promise<AxiosResponse<{
    message: string
}>> => {
    return apiClient.delete(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}`);
};

export const getBoats = (tripUuid: string, offerUuid: string, portUuid: string): Promise<AxiosResponse<any[]>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}/boats`);
};

export const getBoatDetail = (tripUuid: string, offerUuid: string, portUuid: string, boatUuid: string): Promise<AxiosResponse<any>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}/boats/${boatUuid}`);
};

export const createBoat = (tripUuid: string, offerUuid: string, portUuid: string, boatUuid: string, boatData: Partial<any>): Promise<AxiosResponse<any>> => {
    return apiClient.post(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}/boats/${boatUuid}`, boatData);
};

export const updateBoat = (tripUuid: string, offerUuid: string, portUuid: string, boatUuid: string, boatData: Partial<any>): Promise<AxiosResponse<any>> => {
    return apiClient.patch(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}/boats/${boatUuid}`, boatData);
};

export const deleteBoat = (tripUuid: string, offerUuid: string, portUuid: string, boatUuid: string): Promise<AxiosResponse<{
    message: string
}>> => {
    return apiClient.delete(`/api/v1/trips/${tripUuid}/saved-offers/${offerUuid}/port/${portUuid}/boats/${boatUuid}`);
};

export const getBoat = (uuid: number): Promise<AxiosResponse<Yacht>> => {
    return apiClient2.get(`/yacht/${uuid}`);
};

export const sendRequest = (data: {
    boatUuid: string;
    tripUuid: string;
    offerUuid: string;
    portUuid: string;
    addons: any;
    // totalPrice: number;
    startDate: string | null;
    endDate: string | null;
    passengers: number;
    cabins: number;
}): Promise<AxiosResponse<any>> => {
    return apiClient.post(`/api/v1/send-request`, data);
};

export const deleteSingleBoat = (uuid: string): Promise<AxiosResponse<{ message: string }>> => {
    return apiClient.delete(`/api/v1/boat/${uuid}`);
};

export const updateSingleBoat = (uuid: string, boatData: Partial<any>): Promise<AxiosResponse<any>> => {
    return apiClient.patch(`/api/v1/boat/${uuid}`, boatData);
};


export const getDestination = (id: number): Promise<AxiosResponse<{ id: number; name: string }>> => {
    return apiClient2.get(`/geolocation/${id}`);
}

export const searchRecommendations = (query: string): Promise<AxiosResponse<{
    _embedded: { geolocations: { id: number; name: string; entity: string }[] }
}>> => {
    return apiClient2.get(`/searchable/search/findByNameContaining?name=${query}`);
};


export const searchCombinedRecommendations = (query: string): Promise<{
    geolocations: ILocation[]
}> => {
    return Promise.all([
        apiClient2.get(`/searchable/search/findByNameContaining?name=${query}`),
        // apiClient2.get(`/yachtModel/search/findByNameContaining?name=${query}`),
        // apiClient2.get(`/charterCompany/search/findByNameContaining?name=${query}`),
    ]).then(([geoResponse]) => {
        const geolocations = geoResponse.data;
        // const yachtModels = yachtResponse.data._embedded.yachtModels.map(item => ({ ...item, entity: 'yachtModel' }));
        // const charterCompanies = charterResponse.data._embedded.charterCompanies.map(item => ({ ...item, entity: 'charterCompany' }));
        return {geolocations};
    });

};

export const getPopularDestinations = ()=>{
    return apiClient2.get(`/searchable/search/findPopular`)
};


export const getBoatTypes = (): Promise<AxiosResponse<string[]>> => {
    return apiClient.get("/api/v1/boats/types")
}

export const getLayoutsForType = (type: string): Promise<AxiosResponse<Layout[]>> => {
    return apiClient.get(`/api/v1/boats/${type}/layout`)
}

export const searchBoats = (searchParams: AvailabilityQuery): Promise<AxiosResponse<AvailabilityBoat[]>> => {

    const params = new URLSearchParams();
    for (const key in searchParams) {
        if (searchParams[key]) {
            params.append(key, searchParams[key]);
        }
    }
    // return apiClient2.get(`/availability?${params}&page=0&size=50`)
    return apiClient2.get(`/bundle?${params}`)
}


export const getBoatFilters = (searchParams: any): Promise<AxiosResponse<BoatFilters>> => {
    return apiClient.post(`/api/v1/boats/filters`, searchParams)
}

export const getTripItinerary = (tripUuid: string): Promise<AxiosResponse<Route[]>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/itinerary`);
}

export const getCalendarOffers = (tripUuid: string): Promise<AxiosResponse<{
    offers: CalendarOffer[],
    boats: Boat[]
}>> => {
    return apiClient.get(`/api/v1/trips/${tripUuid}/saved-offers/calendar`);
}

export const createCrewList = (crewListName: string) => {
    return apiClient.post(`/api/v1/crew/list`, {name: crewListName});
}

export const getFiltersUsingIds = (
    countryId: number[],
    findByType: string,
    type: string
): Promise<AxiosResponse<{ _embedded: { countries: { id: number; name: string }[] } }>> => {
    // Case sensitive
    // valid findByType 'Country' | 'Region' | 'Location'
    // valid type 'country' | 'region' | 'location'

    return apiClient2.get(`/${type}/search/findBy${findByType}?ids=${countryId}`);
}

export const getAffiliations = (): Promise<AxiosResponse<string, IAffiliations[]>> => {
    return apiClient3.get("/partner/affiliates");
}